import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('orb-card', className)} {...rest} />;
  },
);

// `title` is intentionally widened to ReactNode (not the DOM-attribute
// `string` that HTMLAttributes inherits) so callers can pass icons,
// badges, or formatted labels alongside the heading text. Omit
// removes the conflicting native attribute before re-declaring.
interface HeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
}
export function CardHeader({ title, description, actions, className, ...rest }: Readonly<HeaderProps>) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 px-5 py-4 border-b border-border',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0">
        {title && <h3 className="text-base font-semibold text-fg leading-tight">{title}</h3>}
        {description && <p className="text-sm text-fg-muted mt-1">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...rest }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  return <div className={cn('px-5 py-4', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      className={cn('px-5 py-3 border-t border-border bg-bg-inset/50 rounded-b-lg', className)}
      {...rest}
    />
  );
}
